'use client';

import { useState, useEffect, memo } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
    const [currentYear, setCurrentYear] = useState('2025');

    useEffect(() => {
        setCurrentYear(new Date().getFullYear().toString());
    }, []);

    const footerLinks = {
        platform: [
            { label: 'For Students', href: '/for-students' },
            { label: 'For Colleges', href: '/for-colleges' },
            { label: 'Features', href: '/#features' },
            { label: 'Pricing', href: '/for-colleges#pricing' },
            { label: 'Success Stories', href: '/for-colleges#case-studies' },
        ],
        company: [
            { label: 'About Us', href: '/about' },
            { label: 'Our Mission', href: '/about#mission' },
            { label: 'Careers', href: '/about#careers' },
            { label: 'Contact', href: '/about#contact' },
        ],
        resources: [
            { label: 'Blog', href: '#' },
            { label: 'Help Center', href: '#' },
            { label: 'API Documentation', href: '#' },
            { label: 'Community', href: '#' },
        ],
        legal: [
            { label: 'Privacy Policy', href: '/legal/privacy-policy' },
            { label: 'Terms of Service', href: '/legal/terms-of-service' },
            { label: 'Security', href: '/legal/security' },
            { label: 'Cookie Policy', href: '/legal/cookie-policy' },
        ],
    };

    const socialLinks = [
        { name: 'Twitter', icon: 'ChatBubbleLeftRightIcon', href: '#' },
        { name: 'LinkedIn', icon: 'UserGroupIcon', href: '#' },
        { name: 'Facebook', icon: 'ChatBubbleLeftRightIcon', href: '#' },
        { name: 'Instagram', icon: 'PhotoIcon', href: '#' },
    ];

    return (
        <footer className="bg-[#05050A] border-t border-white/10 pt-20 pb-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
                    {/* Brand Section */}
                    <div className="lg:col-span-2 space-y-6">
                        <Link href="/" className="flex items-center gap-2 group">
                            <div className="relative">
                                <Icon name="AcademicCapIcon" size={32} variant="solid" className="text-primary group-hover:scale-110 transition-transform" />
                            </div>
                            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-success">
                                AstikLearn
                            </span>
                        </Link>
                        <p className="text-text-tertiary max-w-sm leading-relaxed">
                            Empowering the next generation of professionals with AI-driven insights, personalized learning paths, and verifiable credentials.
                        </p>
                        <div className="flex gap-4">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.name}
                                    href={social.href}
                                    className="w-10 h-10 rounded-full glass glass-border flex items-center justify-center text-text-secondary hover:text-white hover:bg-white/10 transition-all hover-lift"
                                    aria-label={social.name}
                                >
                                    <Icon name={social.icon as any} size={20} variant="outline" />
                                </a>
                            ))}
                        </div>

                    </div>

                    {/* Link Columns */}
                    <div>
                        <h4 className="font-bold text-white mb-6">Platform</h4>
                        <ul className="space-y-4 text-text-secondary">
                            {footerLinks.platform.map((link) => (
                                <li key={link.label}>
                                    <Link
                                        href={link.href}
                                        className="hover:text-primary transition-colors text-sm"
                                        prefetch={true}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold text-white mb-6">Company</h4>
                        <ul className="space-y-4 text-text-secondary">
                            {footerLinks.company.map((link) => (
                                <li key={link.label}>
                                    <Link
                                        href={link.href}
                                        className="hover:text-primary transition-colors text-sm"
                                        prefetch={true}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold text-white mb-6">Legal</h4>
                        <ul className="space-y-4 text-text-secondary">
                            {footerLinks.legal.map((link) => (
                                <li key={link.label}>
                                    <Link
                                        href={link.href}
                                        className="hover:text-primary transition-colors text-sm"
                                        prefetch={true}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-text-tertiary text-sm">
                        &copy; {currentYear} AstikLearn. All rights reserved.
                    </div>

                    <div className="pt-4">
                        <h4 className="text-sm font-semibold text-white mb-3">Subscribe to our newsletter</h4>
                        <form className="flex flex-row w-[250px] h-[35px] rounded-[5px] shadow-[4px_4px_#FEFEFE]">
                            <input
                                className="w-full h-full px-[10px] py-[5px] border-2 border-[#FEFEFE] border-r-0 rounded-l-[5px] text-[15px] font-medium text-[#FEFEFE] bg-[#111] focus:outline-none focus:border-[#2d8cf0] focus:border-r-0 placeholder:text-[#666]"
                                placeholder="example@mail.com"
                                type="email"
                            />
                            <button className="w-[100px] h-full border-2 border-[#2d8cf0] rounded-r-[5px] bg-[#2d8cf0] text-[15px] tracking-[1px] font-medium text-[#FEFEFE] cursor-pointer hover:bg-opacity-90 transition-colors">
                                Submit
                            </button>
                        </form>
                    </div>

                </div>
            </div>
        </footer>
    );
};

export default memo(Footer);
